import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { KNOWN_ORGS } from '../config/organizations'

interface DomainOrganization {
  id: string
  name: string
  subdomain: string | null
  settings: {
    branding?: {
      brandName?: string
      primaryColor?: string
      backgroundColor?: string
      accentColor?: string
      textColor?: string
      darkMode?: boolean
    }
    email?: {
      fromName?: string
    }
  }
}

export function useOrganizationFromDomain() {
  const [organization, setOrganization] = useState<DomainOrganization | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchOrganizationByDomain() {
      try {
        const hostname = window.location.hostname
        console.log('Detecting organization for hostname:', hostname)

        // Extract subdomain
        let subdomain = null

        // Check if it's a subdomain setup (e.g., org.aifacilitymanager.com)
        const parts = hostname.split('.')
        if (parts.length > 2 && (hostname.includes('aifacilitymanager') || hostname.includes('localhost'))) {
          subdomain = parts[0]
        }

        // For localhost development, check for subdomain in format: subdomain.localhost:port
        if (hostname.includes('localhost') && parts.length > 1) {
          subdomain = parts[0]
        }

        // Special handling for known domains
        if (hostname === 'atn.localhost' || hostname.includes('atn.')) {
          subdomain = 'atn'
        }

        console.log('Subdomain:', subdomain, 'Hostname:', hostname)

        // Try to fetch organization by subdomain with explicit headers
        let query = supabase
          .from('organizations')
          .select('id, name, subdomain, settings')

        if (subdomain) {
          query = query.eq('subdomain', subdomain)
        } else {
          // Default organization for main domain
          query = query.eq('id', KNOWN_ORGS.DEFAULT)
        }

        // Execute query with maybeSingle (handles 0 or 1 results)
        const { data, error: fetchError } = await query.maybeSingle()

        if (fetchError) {
          console.error('Error fetching organization:', {
            message: fetchError.message,
            code: fetchError.code,
            details: fetchError.details,
            hint: fetchError.hint,
            subdomain,
            hostname
          })

          // Handle 406 specifically
          if (fetchError.code === 'PGRST116' || fetchError.message?.includes('406')) {
            console.error('406 Not Acceptable - Content negotiation failed')
            console.error('This may indicate an RLS policy issue or malformed query')
          }

          setError(`Failed to detect organization: ${fetchError.message}`)
          return
        }

        if (!data) {
          console.log('No organization found for subdomain, falling back to default')
          // Fall back to default organization
          const { data: defaultOrg, error: defaultError } = await supabase
            .from('organizations')
            .select('id, name, subdomain, settings')
            .eq('id', KNOWN_ORGS.DEFAULT)
            .maybeSingle()

          if (defaultError) {
            console.error('Error fetching default organization:', {
              message: defaultError.message,
              code: defaultError.code,
              details: defaultError.details
            })
            setError(`Failed to fetch default organization: ${defaultError.message}`)
            return
          }

          if (!defaultOrg) {
            console.error('No default organization found in database')
            setError('No organization found')
            return
          }

          console.log('Using default organization:', defaultOrg.name)
          setOrganization(defaultOrg)
        } else {
          console.log('Found organization:', data.name)
          setOrganization(data)
        }
      } catch (err) {
        console.error('Error in fetchOrganizationByDomain:', err)
        setError(err instanceof Error ? err.message : 'Failed to detect organization')
      } finally {
        setLoading(false)
      }
    }

    fetchOrganizationByDomain()
  }, [])

  return { organization, loading, error }
}